import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { iamApi } from '@/api/client'
import type { PaginatedResponse, SiteResponse } from '@/api/types'
import { Plus, Pencil, Trash2 } from 'lucide-react'

export function SitesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [sites, setSites] = useState<SiteResponse[]>([])
  const [_total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchSites = () => {
    setLoading(true)
    iamApi.get<PaginatedResponse<SiteResponse>>('/sites', { params: { limit: 50 } })
      .then(({ data }) => {
        setSites(data.data || [])
        setTotal(data.total || 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchSites() }, [])

  const handleDelete = async (id: number) => {
    if (!confirm(t('sites.confirmDelete'))) return
    try {
      await iamApi.delete(`/sites/${id}`)
      fetchSites()
    } catch { /* empty */ }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">{t('sites.title')}</h1>
        <button
          onClick={() => navigate('/sites/new')}
          className="flex items-center gap-2 rounded-lg bg-accent text-accent-foreground px-4 py-2 text-sm font-medium hover:bg-accent-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          {t('sites.create')}
        </button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">{t('common.loading')}</p>
      ) : sites.length === 0 ? (
        <p className="text-muted-foreground">{t('sites.noSites')}</p>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary">
                <th className="text-left text-xs font-semibold text-muted-foreground px-5 py-3">{t('sites.name')}</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-5 py-3">{t('sites.slug')}</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-5 py-3">{t('sites.domain')}</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-5 py-3">{t('sites.status')}</th>
                <th className="text-right text-xs font-semibold text-muted-foreground px-5 py-3">{t('sites.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sites.map((site) => (
                <tr key={site.id} className="hover:bg-secondary/50 transition-colors">
                  <td className="px-5 py-3">
                    <Link to={`/sites/${site.id}`} state={{ tenantId: site.tenant_id }} className="font-medium text-foreground hover:text-accent transition-colors">
                      {site.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-sm text-muted-foreground">{site.slug}</td>
                  <td className="px-5 py-3 text-sm text-muted-foreground">{site.domain || '\u2014'}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      site.status === 'active' ? 'bg-green-100 text-green-700' :
                      site.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                      site.status === 'maintenance' ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {site.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/sites/${site.id}`} state={{ tenantId: site.tenant_id }} className="text-muted-foreground hover:text-foreground p-1 rounded transition-colors">
                        <Pencil className="h-4 w-4" />
                      </Link>
                      <button onClick={() => handleDelete(site.id)} className="text-muted-foreground hover:text-destructive p-1 rounded transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
